import { useState, useRef } from 'react';
import { X, ChevronDown } from 'lucide-react';
import type { FilterFieldDef, FilterOperator } from './schema';
import type { ActiveFilter } from './parser';
import { FilterPopover } from './FilterPopover';
import { TextFilterEditor } from './editors/TextFilterEditor';
import { EnumFilterEditor } from './editors/EnumFilterEditor';
import { RangeFilterEditor } from './editors/RangeFilterEditor';
import { DateRangeFilterEditor } from './editors/DateRangeFilterEditor';
import { useI18n } from '@/context/I18nContext';

interface FilterChipProps {
  fieldDef: FilterFieldDef;
  filter: ActiveFilter;
  onUpdate: (updates: Partial<ActiveFilter>) => void;
  onRemove: () => void;
}

export function FilterChip({
  fieldDef,
  filter,
  onUpdate,
  onRemove,
}: FilterChipProps) {
  const { t } = useI18n();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const chipRef = useRef<HTMLDivElement>(null);

  const fieldLabel = t(fieldDef.label as any);
  const unitLabel = fieldDef.unit ? t(fieldDef.unit as any) : '';

  const resolveOptionLabel = (val: string) => {
    if (Array.isArray(fieldDef.options)) {
      const opt = fieldDef.options.find((o) => o.value === val);
      if (opt) return t(opt.label as any);
    }
    return val;
  };

  const formatDisplayValue = () => {
    if (filter.values && filter.values.length > 0) {
      if (filter.values.length === 1) return resolveOptionLabel(filter.values[0]);
      return `${resolveOptionLabel(filter.values[0])} +${filter.values.length - 1}`;
    }

    if (filter.operator === 'between' && filter.highValue) {
      return `${filter.value}..${filter.highValue}${unitLabel ? ' ' + unitLabel : ''}`;
    }

    if (filter.operator && filter.operator !== '=') {
      const opSymbol =
        filter.operator === '>='
          ? '≥'
          : filter.operator === '<='
          ? '≤'
          : filter.operator;
      return `${opSymbol} ${filter.value}${unitLabel ? ' ' + unitLabel : ''}`;
    }

    // Date formatting friendly labels if known preset or absolute range
    if (fieldDef.type === 'date' || fieldDef.type === 'dateRange') {
      if (filter.value === 'today') return t('filter.preset.today' as any);
      if (filter.value === '-7d') return t('filter.preset.last7dShort' as any);
      if (filter.value === '-30d') return t('filter.preset.last30dShort' as any);
      if (filter.value === '-90d') return t('filter.preset.last90dShort' as any);
      if (filter.value === '-1y') return t('filter.preset.last1yShort' as any);
      if (filter.value && filter.highValue) return `${filter.value} .. ${filter.highValue}`;
      if (filter.value) return `${t('filter.from').toLowerCase()} ${filter.value}`;
    }

    if (fieldDef.type === 'enum') {
      return resolveOptionLabel(filter.value);
    }

    return `${filter.value}${unitLabel ? ' ' + unitLabel : ''}`;
  };

  const Icon = fieldDef.icon;

  return (
    <div ref={chipRef} className="relative inline-flex items-center">
      <div
        className={`inline-flex items-center gap-1.5 pl-3 pr-2 py-1.5 sm:pl-2.5 sm:pr-1.5 sm:py-1 rounded-lg sm:rounded-md text-xs font-medium
          bg-bg-surface border transition-colors cursor-pointer select-none
          ${
            popoverOpen
              ? 'border-accent text-accent shadow-sm'
              : 'border-border-default hover:border-border-hover text-text-primary'
          }`}
        onClick={() => setPopoverOpen((prev) => !prev)}
      >
        {Icon && <Icon size={13} className="text-text-muted shrink-0" />}
        <span className="text-text-secondary">{fieldLabel}:</span>
        <span className="text-text-primary font-medium truncate max-w-[180px] sm:max-w-[140px]">
          {formatDisplayValue()}
        </span>
        <ChevronDown size={12} className="text-text-muted shrink-0 ml-0.5" />
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="p-1 sm:p-0.5 rounded-md hover:bg-bg-hover text-text-muted hover:text-text-primary transition-colors cursor-pointer shrink-0 ml-1"
          aria-label={t('filter.removeFilter' as any, { label: fieldLabel })}
        >
          <X size={13} />
        </button>
      </div>

      <FilterPopover
        open={popoverOpen}
        onClose={() => setPopoverOpen(false)}
        title={t('filter.filterBy' as any, { label: fieldLabel })}
        triggerRef={chipRef}
      >
        {fieldDef.type === 'enum' ? (
          <EnumFilterEditor
            fieldDef={fieldDef}
            filter={filter}
            onApply={(vals) => {
              if (vals.length === 0) {
                onRemove();
              } else if (vals.length === 1) {
                onUpdate({ value: vals[0], values: undefined, operator: '=' });
              } else {
                onUpdate({ value: vals.join(', '), values: vals, operator: '=' });
              }
            }}
            onClose={() => setPopoverOpen(false)}
          />
        ) : fieldDef.type === 'range' || fieldDef.type === 'number' ? (
          <RangeFilterEditor
            fieldDef={fieldDef}
            filter={filter}
            onApply={(val, op: FilterOperator, highVal) => {
              onUpdate({ value: val, operator: op, highValue: highVal });
            }}
            onClose={() => setPopoverOpen(false)}
          />
        ) : fieldDef.type === 'date' || fieldDef.type === 'dateRange' ? (
          <DateRangeFilterEditor
            fieldDef={fieldDef}
            filter={filter}
            onApply={(fromVal, toVal) => {
              onUpdate({ value: fromVal, highValue: toVal, operator: '=' });
            }}
            onClose={() => setPopoverOpen(false)}
          />
        ) : (
          <TextFilterEditor
            fieldDef={fieldDef}
            filter={filter}
            onApply={(val) => {
              onUpdate({ value: val, operator: '=' });
            }}
            onClose={() => setPopoverOpen(false)}
          />
        )}
      </FilterPopover>
    </div>
  );
}
